import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { employerAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import VerificationRequiredModal from '../../components/common/VerificationRequiredModal';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, Briefcase, FileText, Star, 
  Loader2, Check, Plus, X, Info, ClipboardList,
  Users, TrendingUp, Award, MessageSquare, Shield, AlertTriangle
} from 'lucide-react';

const TOTAL_STEPS = 8;
const AUTOSAVE_INTERVAL = 30000;

// ==================== EXTRACTED STABLE COMPONENTS ====================
// These are defined OUTSIDE the main component to prevent re-creation on each render

const StarRating = memo(({ value, onChange, label }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-[#0A0F1E] text-sm">{label}</span>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="p-0.5 transition-transform hover:scale-110"
        >
          <Star className={`w-6 h-6 ${
            star <= value 
              ? 'fill-[#1B5AFF] text-[#1B5AFF]' 
              : 'text-[#5B6472] hover:text-[#1B5AFF]/50'
          }`} />
        </button>
      ))}
    </div>
  </div>
));
StarRating.displayName = 'StarRating';

const CharCounter = memo(({ current, max }) => (
  <p className={`text-xs text-right mt-1 ${current > max * 0.9 ? 'text-amber-400' : 'text-[#5B6472]'}`}>
    {current}/{max}
  </p>
));
CharCounter.displayName = 'CharCounter';

// Stable list item component - uses unique ID for key instead of index
const ListItemInput = memo(({ id, index, value, placeholder, onChange, onRemove, canRemove }) => (
  <div className="flex items-start gap-2">
    <div className="w-6 h-10 flex items-center justify-center text-[#5B6472] text-sm">
      {index + 1}.
    </div>
    <Input
      value={value}
      onChange={(e) => onChange(id, e.target.value)}
      placeholder={placeholder}
      className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-10 flex-1"
    />
    {canRemove && (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onRemove(id)}
        className="h-10 w-10 p-0 text-[#5B6472] hover:text-red-400"
      >
        <X className="w-4 h-4" />
      </Button>
    )}
  </div>
));
ListItemInput.displayName = 'ListItemInput';

// ==================== MAIN COMPONENT ====================

const RecordNew = () => {
  const navigate = useNavigate();
  const { id: recordId } = useParams();
  const isEditMode = !!recordId;
  const { emailVerified } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [lastSaved, setLastSaved] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  const [recordStatus, setRecordStatus] = useState(null);

  // Use objects with stable IDs for list items to prevent focus loss
  const [responsibilities, setResponsibilities] = useState([
    { id: 'resp-1', value: '' },
    { id: 'resp-2', value: '' },
    { id: 'resp-3', value: '' }
  ]);
  
  const [achievements, setAchievements] = useState([
    { id: 'ach-1', value: '' }
  ]);

  const [formData, setFormData] = useState({
    employee_email: '',
    employee_name: '',
    job_title: '',
    department: '',
    employment_type: '',
    start_date: '',
    end_date: '',
    currently_employed: false,
    reporting_to: '',
    rating: 0,
    punctuality_rating: 0,
    deadline_rating: 0,
    initiative_rating: 0,
    adaptability_rating: 0,
    work_ethic_note: '',
    skills: [],
    strengths: '',
    growth_areas: '',
    peer_team_perception: '',
    collaboration_style: '',
    collaboration_note: '',
    reason_for_departure: '',
    departure_note: '',
    rehire_eligibility: '',
    rehire_note: '',
    closing_statement: '',
    compliance_acknowledged: false,
    public_note: '',
    internal_notes: '',
    include_internal_notes: true,
  });

  // Counter for generating unique IDs
  const [idCounter, setIdCounter] = useState(10);
  const generateId = useCallback((prefix) => {
    setIdCounter(prev => prev + 1);
    return `${prefix}-${idCounter}`;
  }, [idCounter]);

  // Autosave
  const saveDraft = useCallback(async () => {
    if (!formData.employee_email) return;
    setLastSaved(new Date());
  }, [formData.employee_email]);

  useEffect(() => {
    const interval = setInterval(saveDraft, AUTOSAVE_INTERVAL);
    return () => clearInterval(interval);
  }, [saveDraft]);

  // Load existing record data if editing
  useEffect(() => {
    if (isEditMode && recordId) {
      loadRecordData();
    }
  }, [isEditMode, recordId]);

  const loadRecordData = async () => {
    setInitialLoading(true);
    try {
      const res = await employerAPI.getRecord(recordId);
      const record = res.data;
      
      // Check if record is immutable (acknowledged)
      if (record.status === 'acknowledged') {
        toast.error('Co-signed records cannot be edited');
        navigate('/employer/records');
        return;
      }
      
      setRecordStatus(record.status);
      
      // Populate form data
      setFormData({
        employee_email: record.employee_email || '',
        employee_name: record.employee_name || '',
        job_title: record.job_title || '',
        department: record.department || '',
        employment_type: record.employment_type || '',
        start_date: record.start_date?.split('T')[0] || '',
        end_date: record.end_date?.split('T')[0] || '',
        currently_employed: record.currently_employed || false,
        reporting_to: record.reporting_to || '',
        rating: record.rating || 0,
        punctuality_rating: record.punctuality_rating || 0,
        deadline_rating: record.deadline_rating || 0,
        initiative_rating: record.initiative_rating || 0,
        adaptability_rating: record.adaptability_rating || 0,
        work_ethic_note: record.work_ethic_note || '',
        skills: record.skills || [],
        strengths: record.strengths || '',
        growth_areas: record.growth_areas || '',
        peer_team_perception: record.peer_team_perception || '',
        collaboration_style: record.collaboration_style || '',
        collaboration_note: record.collaboration_note || '',
        reason_for_departure: record.reason_for_departure || '',
        departure_note: record.departure_note || '',
        rehire_eligibility: record.rehire_eligibility || '',
        rehire_note: record.rehire_note || '',
        closing_statement: record.closing_statement || '',
        compliance_acknowledged: record.compliance_acknowledged || false,
        public_note: record.public_note || '',
        internal_notes: record.internal_notes || '',
        include_internal_notes: record.include_internal_notes !== false,
      });
      
      // Populate responsibilities
      if (record.daily_responsibilities?.length > 0) {
        setResponsibilities(record.daily_responsibilities.map((item, i) => ({
          id: `resp-loaded-${i}`,
          value: item
        })));
      }
      
      // Populate achievements
      if (record.key_achievements?.length > 0) {
        setAchievements(record.key_achievements.map((item, i) => ({
          id: `ach-loaded-${i}`,
          value: item
        })));
      }
      
    } catch (error) {
      console.error('Error loading record:', error);
      toast.error('Failed to load record');
      navigate('/employer/records');
    } finally {
      setInitialLoading(false);
    }
  };

  // Form field change handler
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Responsibility handlers
  const handleResponsibilityChange = useCallback((id, value) => {
    setResponsibilities(prev => prev.map(item => 
      item.id === id ? { ...item, value } : item
    ));
  }, []);

  const handleAddResponsibility = useCallback(() => {
    if (responsibilities.length < 15) {
      setResponsibilities(prev => [...prev, { id: generateId('resp'), value: '' }]);
    }
  }, [responsibilities.length, generateId]);

  const handleRemoveResponsibility = useCallback((id) => {
    if (responsibilities.length > 3) {
      setResponsibilities(prev => prev.filter(item => item.id !== id));
    }
  }, [responsibilities.length]);

  // Achievement handlers
  const handleAchievementChange = useCallback((id, value) => {
    setAchievements(prev => prev.map(item => 
      item.id === id ? { ...item, value } : item
    ));
  }, []);

  const handleAddAchievement = useCallback(() => {
    if (achievements.length < 8) {
      setAchievements(prev => [...prev, { id: generateId('ach'), value: '' }]);
    }
  }, [achievements.length, generateId]);

  const handleRemoveAchievement = useCallback((id) => {
    if (achievements.length > 1) {
      setAchievements(prev => prev.filter(item => item.id !== id));
    }
  }, [achievements.length]);

  // Skills handlers
  const handleAddSkill = useCallback((skill) => {
    if (skill && !formData.skills.includes(skill)) {
      setFormData(prev => ({ ...prev, skills: [...prev.skills, skill] }));
    }
    setSkillInput('');
  }, [formData.skills]);

  const handleRemoveSkill = useCallback((skill) => {
    setFormData(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  }, []);

  // Rating handlers
  const handleRatingChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Validation
  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        if (!formData.employee_email) {
          toast.error('Please enter an email address');
          return false;
        }
        if (!formData.job_title) {
          toast.error('Please enter a job title');
          return false;
        }
        if (!formData.start_date) {
          toast.error('Please enter a start date');
          return false;
        }
        return true;
      case 2:
        const filledResponsibilities = responsibilities.filter(r => r.value.trim());
        if (filledResponsibilities.length < 3) {
          toast.error('Please add at least 3 daily responsibilities');
          return false;
        }
        return true;
      case 3:
        if (!formData.rating) {
          toast.error('Please provide an overall performance rating');
          return false;
        }
        return true;
      case 4:
        const filledAchievements = achievements.filter(a => a.value.trim());
        if (filledAchievements.length < 1) {
          toast.error('Please add at least 1 key achievement');
          return false;
        }
        return true;
      case 5:
        return true;
      case 6:
        // Skip validation if currently employed - departure/rehire not applicable
        if (formData.currently_employed) {
          return true;
        }
        if (!formData.rehire_eligibility) {
          toast.error('Please select a rehire eligibility option');
          return false;
        }
        // Require mandatory reason with 50 chars minimum when No is selected
        if (formData.rehire_eligibility === 'no') {
          if (!formData.rehire_note || formData.rehire_note.trim().length < 50) {
            toast.error('When selecting "No" for rehire eligibility, you must provide a reason with at least 50 characters');
            return false;
          }
        } else if (!formData.rehire_note) {
          toast.error('Please explain your rehire decision');
          return false;
        }
        return true;
      case 7:
        if (!formData.closing_statement) {
          toast.error('Please write a closing statement');
          return false;
        }
        return true;
      case 8:
        if (!formData.compliance_acknowledged) {
          toast.error('Please acknowledge the compliance guidelines');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, TOTAL_STEPS));
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (asDraft = false) => {
    if (!asDraft && !emailVerified) {
      setShowVerificationModal(true);
      return;
    }
    
    if (!asDraft && !validateStep(8)) return;
    
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        daily_responsibilities: responsibilities.map(r => r.value).filter(v => v.trim()),
        key_achievements: achievements.map(a => a.value).filter(v => v.trim()),
      };
      
      if (isEditMode && recordId) {
        // Update existing record
        await employerAPI.updateRecord(recordId, submitData);
        
        // If record was pending and we're publishing, submit it again
        if (!asDraft && recordStatus === 'pending_acknowledgment') {
          await employerAPI.submitRecord(recordId);
        }
        
        toast.success(asDraft ? 'Record updated as draft' : 'Record updated and employee re-notified!');
      } else {
        // Create new record
        await employerAPI.createRecord(submitData);
        toast.success(asDraft ? 'Record saved as draft' : 'Record submitted for employee acknowledgment!');
      }
      
      navigate('/employer/records');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save record');
    } finally {
      setLoading(false);
    }
  };

  const responsibilityPlaceholders = [
    "e.g. Managed weekly client calls with 8 enterprise accounts",
    "e.g. Owned the editorial calendar and published 3 pieces per week",
    "e.g. Ran daily standups and sprint planning for a team of 6",
    "Add another responsibility..."
  ];

  const achievementPlaceholders = [
    "e.g. Grew email list from 4,000 to 22,000 in 8 months",
    "e.g. Reduced customer churn by 15% through improved onboarding",
    "Add another achievement..."
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 sm:p-8 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A0F1E]">Role Overview</h2>
                <p className="text-[#5B6472] text-sm">Basic employment information</p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-[#0A0F1E]">Employee Email *</Label>
                <Input
                  type="email"
                  value={formData.employee_email}
                  onChange={(e) => handleChange('employee_email', e.target.value)}
                  placeholder="employee@example.com"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  data-testid="employee-email-input"
                />
              </div>
              
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-[#0A0F1E]">Employee Full Name</Label>
                <Input
                  value={formData.employee_name}
                  onChange={(e) => handleChange('employee_name', e.target.value)}
                  placeholder="John Doe"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                />
              </div>
              
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-[#0A0F1E]">Job Title *</Label>
                <Input
                  value={formData.job_title}
                  onChange={(e) => handleChange('job_title', e.target.value)}
                  placeholder="Senior Software Engineer"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  data-testid="job-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Department</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  placeholder="Engineering"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Employment Type</Label>
                <Select value={formData.employment_type} onValueChange={(v) => handleChange('employment_type', v)}>
                  <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    <SelectItem value="full-time" className="text-[#0A0F1E]">Full-time</SelectItem>
                    <SelectItem value="part-time" className="text-[#0A0F1E]">Part-time</SelectItem>
                    <SelectItem value="contract" className="text-[#0A0F1E]">Contract</SelectItem>
                    <SelectItem value="freelance" className="text-[#0A0F1E]">Freelance</SelectItem>
                    <SelectItem value="internship" className="text-[#0A0F1E]">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  data-testid="start-date-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleChange('end_date', e.target.value)}
                  disabled={formData.currently_employed}
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12 disabled:opacity-50"
                />
              </div>
              
              <div className="sm:col-span-2 flex items-center gap-3">
                <Switch
                  checked={formData.currently_employed}
                  onCheckedChange={(checked) => handleChange('currently_employed', checked)}
                />
                <Label className="text-[#0A0F1E]">Currently employed here</Label>
              </div>
              
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-[#0A0F1E]">Direct Report Line</Label>
                <Input
                  value={formData.reporting_to}
                  onChange={(e) => handleChange('reporting_to', e.target.value)}
                  placeholder="e.g. VP of Engineering"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                />
              </div>
            </div>
          </Card>
        );

      case 2:
        return (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 sm:p-8 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A0F1E]">Daily Responsibilities</h2>
                <p className="text-[#5B6472] text-sm">What did this person do day-to-day? (3-15 items)</p>
              </div>
            </div>
            
            <div className="space-y-3">
              {responsibilities.map((item, index) => (
                <ListItemInput
                  key={item.id}
                  id={item.id}
                  index={index}
                  value={item.value}
                  placeholder={responsibilityPlaceholders[Math.min(index, responsibilityPlaceholders.length - 1)]}
                  onChange={handleResponsibilityChange}
                  onRemove={handleRemoveResponsibility}
                  canRemove={responsibilities.length > 3}
                />
              ))}
              {responsibilities.length < 15 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddResponsibility}
                  className="w-full border-dashed border-[#E8EDF5] text-[#5B6472] hover:text-[#0A0F1E] hover:border-[#1B5AFF]/50 rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              )}
            </div>
          </Card>
        );

      case 3:
        return (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 sm:p-8 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center">
                <Star className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A0F1E]">Performance & Work Ethic</h2>
                <p className="text-[#5B6472] text-sm">Rate their overall performance and reliability</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                <Label className="text-[#0A0F1E] text-base font-medium mb-3 block">Overall Performance Rating *</Label>
                <div className="flex gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => handleRatingChange('rating', rating)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`rating-${rating}`}
                    >
                      <Star className={`w-10 h-10 ${
                        rating <= formData.rating 
                          ? 'fill-[#1B5AFF] text-[#1B5AFF]' 
                          : 'text-[#5B6472] hover:text-[#1B5AFF]/50'
                      }`} />
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E] text-base font-medium">Work Ethic Dimensions</Label>
                <div className="p-4 rounded-xl bg-[#F5F8FF]/30 border border-[#E8EDF5] space-y-1">
                  <StarRating
                    value={formData.punctuality_rating}
                    onChange={(v) => handleRatingChange('punctuality_rating', v)}
                    label="Punctuality & Attendance"
                  />
                  <StarRating
                    value={formData.deadline_rating}
                    onChange={(v) => handleRatingChange('deadline_rating', v)}
                    label="Deadline Reliability"
                  />
                  <StarRating
                    value={formData.initiative_rating}
                    onChange={(v) => handleRatingChange('initiative_rating', v)}
                    label="Initiative & Proactivity"
                  />
                  <StarRating
                    value={formData.adaptability_rating}
                    onChange={(v) => handleRatingChange('adaptability_rating', v)}
                    label="Adaptability to Change"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Work Ethic Note (optional)</Label>
                <Textarea
                  value={formData.work_ethic_note}
                  onChange={(e) => handleChange('work_ethic_note', e.target.value.slice(0, 300))}
                  placeholder="Anything notable about their reliability, drive, or consistency?"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[80px]"
                />
                <CharCounter current={formData.work_ethic_note.length} max={300} />
              </div>
            </div>
          </Card>
        );

      case 4:
        return (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 sm:p-8 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A0F1E]">Skills & Achievements</h2>
                <p className="text-[#5B6472] text-sm">What skills did they demonstrate and what did they accomplish?</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-3">
                <Label className="text-[#0A0F1E]">Skills Demonstrated</Label>
                <div className="flex gap-2">
                  <Input
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSkill(skillInput))}
                    placeholder="Type a skill and press Enter"
                    className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12 flex-1"
                  />
                  <Button type="button" onClick={() => handleAddSkill(skillInput)} className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl h-12">
                    Add
                  </Button>
                </div>
                {formData.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {formData.skills.map((skill, i) => (
                      <span key={skill} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1B5AFF]/10 text-[#1B5AFF] text-sm border border-[#1B5AFF]/30">
                        {skill}
                        <button type="button" onClick={() => handleRemoveSkill(skill)} className="hover:text-[#0A0F1E]">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                <Label className="text-[#0A0F1E]">Key Achievements * (1-8 items)</Label>
                <p className="text-[#5B6472] text-xs">Be specific and measurable where possible</p>
                <div className="space-y-3">
                  {achievements.map((item, index) => (
                    <ListItemInput
                      key={item.id}
                      id={item.id}
                      index={index}
                      value={item.value}
                      placeholder={achievementPlaceholders[Math.min(index, achievementPlaceholders.length - 1)]}
                      onChange={handleAchievementChange}
                      onRemove={handleRemoveAchievement}
                      canRemove={achievements.length > 1}
                    />
                  ))}
                  {achievements.length < 8 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddAchievement}
                      className="w-full border-dashed border-[#E8EDF5] text-[#5B6472] hover:text-[#0A0F1E] hover:border-[#1B5AFF]/50 rounded-xl"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        );

      case 5:
        return (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 sm:p-8 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A0F1E]">Strengths, Growth & Team Dynamics</h2>
                <p className="text-[#5B6472] text-sm">Their strengths, areas for development, and how they worked with others</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Top Strengths</Label>
                <Textarea
                  value={formData.strengths}
                  onChange={(e) => handleChange('strengths', e.target.value.slice(0, 500))}
                  placeholder="What did this person do exceptionally well? Be specific."
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
                />
                <CharCounter current={formData.strengths.length} max={500} />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Areas for Growth</Label>
                <Textarea
                  value={formData.growth_areas}
                  onChange={(e) => handleChange('growth_areas', e.target.value.slice(0, 500))}
                  placeholder="What areas could this person develop further? Be honest and constructive."
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
                />
                <CharCounter current={formData.growth_areas.length} max={500} />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Peer & Team Perception</Label>
                <Textarea
                  value={formData.peer_team_perception}
                  onChange={(e) => handleChange('peer_team_perception', e.target.value.slice(0, 800))}
                  placeholder="How did this person's colleagues experience working with them? What was their reputation on the team?"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[120px]"
                />
                <CharCounter current={formData.peer_team_perception.length} max={800} />
              </div>
              
              <div className="space-y-3">
                <Label className="text-[#0A0F1E]">Collaboration Style</Label>
                <Select value={formData.collaboration_style} onValueChange={(v) => handleChange('collaboration_style', v)}>
                  <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                    <SelectValue placeholder="Select collaboration style" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    <SelectItem value="independent" className="text-[#0A0F1E]">Independent Worker</SelectItem>
                    <SelectItem value="collaborator" className="text-[#0A0F1E]">Strong Collaborator</SelectItem>
                    <SelectItem value="team_leader" className="text-[#0A0F1E]">Natural Team Leader</SelectItem>
                    <SelectItem value="cross_functional" className="text-[#0A0F1E]">Cross-functional Connector</SelectItem>
                    <SelectItem value="mentor" className="text-[#0A0F1E]">Mentorship-Oriented</SelectItem>
                    <SelectItem value="client_facing" className="text-[#0A0F1E]">Client-Facing Specialist</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={formData.collaboration_note}
                  onChange={(e) => handleChange('collaboration_note', e.target.value.slice(0, 150))}
                  placeholder="Add context about their collaboration style (optional)"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-10"
                />
              </div>
            </div>
          </Card>
        );

      case 6:
        // If currently employed, skip departure/rehire fields
        if (formData.currently_employed) {
          return (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 sm:p-8 animate-fade-in">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#0A0F1E]">Active Employment</h2>
                  <p className="text-[#5B6472] text-sm">This employee is currently employed</p>
                </div>
              </div>
              
              <div className="p-6 rounded-xl bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-3">
                  <Check className="w-6 h-6 text-green-400" />
                  <div>
                    <p className="text-green-400 font-medium">Currently Employed</p>
                    <p className="text-[#5B6472] text-sm mt-1">
                      Departure and rehire eligibility fields are not applicable for active employees. 
                      These fields will be added when the employment ends.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          );
        }
        
        return (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 sm:p-8 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A0F1E]">Departure & Rehire</h2>
                <p className="text-[#5B6472] text-sm">Why did they leave and would you rehire them?</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <TooltipProvider>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-[#0A0F1E]">Reason for Departure</Label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-4 h-4 text-[#5B6472]" />
                      </TooltipTrigger>
                      <TooltipContent className="bg-[#F5F8FF] border-[#E8EDF5] text-[#0A0F1E] max-w-xs">
                        <p>This field is visible to employers searching verified talent. The employee can flag this field before the record is published.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Select value={formData.reason_for_departure} onValueChange={(v) => handleChange('reason_for_departure', v)}>
                    <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                      <SelectItem value="resigned" className="text-[#0A0F1E]">Resigned voluntarily</SelectItem>
                      <SelectItem value="mutual" className="text-[#0A0F1E]">Mutual agreement</SelectItem>
                      <SelectItem value="contract_end" className="text-[#0A0F1E]">End of contract</SelectItem>
                      <SelectItem value="redundancy" className="text-[#0A0F1E]">Redundancy or restructure</SelectItem>
                      <SelectItem value="performance" className="text-[#0A0F1E]">Performance-related</SelectItem>
                      <SelectItem value="other" className="text-[#0A0F1E]">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.reason_for_departure === 'other' && (
                    <Input
                      value={formData.departure_note}
                      onChange={(e) => handleChange('departure_note', e.target.value.slice(0, 200))}
                      placeholder="Please specify..."
                      className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12 mt-2"
                    />
                  )}
                </div>
              </TooltipProvider>
              
              <div className="space-y-3">
                <Label className="text-[#0A0F1E]">Rehire Eligibility *</Label>
                <div className="grid gap-3">
                  <button
                    type="button"
                    onClick={() => handleChange('rehire_eligibility', 'yes_without_reservation')}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      formData.rehire_eligibility === 'yes_without_reservation'
                        ? 'bg-green-500/10 border-green-500/50 text-green-400'
                        : 'bg-[#F5F8FF]/30 border-[#E8EDF5] text-[#0A0F1E] hover:border-green-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.rehire_eligibility === 'yes_without_reservation' ? 'border-green-500 bg-green-500' : 'border-[#5B6472]'
                      }`}>
                        {formData.rehire_eligibility === 'yes_without_reservation' && <Check className="w-3 h-3 text-[#0A0F1E]" />}
                      </div>
                      <div>
                        <span className="font-medium">Yes, without reservation</span>
                        <p className="text-sm text-[#5B6472] mt-0.5">I would happily rehire this person</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleChange('rehire_eligibility', 'yes_with_right_role')}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      formData.rehire_eligibility === 'yes_with_right_role'
                        ? 'bg-amber-500/10 border-amber-500/50 text-amber-400'
                        : 'bg-[#F5F8FF]/30 border-[#E8EDF5] text-[#0A0F1E] hover:border-amber-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.rehire_eligibility === 'yes_with_right_role' ? 'border-amber-500 bg-amber-500' : 'border-[#5B6472]'
                      }`}>
                        {formData.rehire_eligibility === 'yes_with_right_role' && <Check className="w-3 h-3 text-[#0A0F1E]" />}
                      </div>
                      <div>
                        <span className="font-medium">Yes, with the right role</span>
                        <p className="text-sm text-[#5B6472] mt-0.5">I would consider rehiring for a suitable position</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleChange('rehire_eligibility', 'no')}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      formData.rehire_eligibility === 'no'
                        ? 'bg-red-500/10 border-red-500/50 text-red-400'
                        : 'bg-[#F5F8FF]/30 border-[#E8EDF5] text-[#0A0F1E] hover:border-red-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.rehire_eligibility === 'no' ? 'border-red-500 bg-red-500' : 'border-[#5B6472]'
                      }`}>
                        {formData.rehire_eligibility === 'no' && <Check className="w-3 h-3 text-[#0A0F1E]" />}
                      </div>
                      <div>
                        <span className="font-medium">No</span>
                        <p className="text-sm text-[#5B6472] mt-0.5">I would not rehire this person</p>
                      </div>
                    </div>
                  </button>
                </div>
                
                {formData.rehire_eligibility && (
                  <div className="mt-4 space-y-2">
                    <Label className="text-[#0A0F1E]">
                      {formData.rehire_eligibility === 'no' 
                        ? 'Explain your decision (minimum 50 characters) *' 
                        : 'Briefly explain your decision *'}
                    </Label>
                    <Textarea
                      value={formData.rehire_note}
                      onChange={(e) => handleChange('rehire_note', e.target.value.slice(0, 500))}
                      placeholder={formData.rehire_eligibility === 'no' 
                        ? "Required: Please provide a detailed explanation for not rehiring (minimum 50 characters)"
                        : "Required explanation for your rehire decision"}
                      className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
                    />
                    <div className="flex justify-between items-center">
                      <CharCounter current={formData.rehire_note.length} max={500} />
                      {formData.rehire_eligibility === 'no' && formData.rehire_note.length < 50 && (
                        <span className="text-red-400 text-xs">Minimum 50 characters required</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        );

      case 7:
        return (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 sm:p-8 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A0F1E]">Professional Assessment</h2>
                <p className="text-[#5B6472] text-sm">Your overall professional view of this person</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <Textarea
                value={formData.closing_statement}
                onChange={(e) => handleChange('closing_statement', e.target.value.slice(0, 1200))}
                placeholder="Write your overall professional assessment of this person. What is your genuine view of them as a professional? What kind of role or team would they thrive in? This is the most important field in the record. Take your time."
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[250px] text-base leading-relaxed"
              />
              <CharCounter current={formData.closing_statement.length} max={1200} />
              
              <div className="p-4 rounded-xl bg-[#F5F8FF]/30 border border-[#E8EDF5]">
                <p className="text-[#5B6472] text-sm leading-relaxed">
                  <strong className="text-[#0A0F1E]">Note:</strong> This is your formal professional assessment of this employee's tenure. Write it with care. It becomes a permanent part of their verified career record and carries significant weight for their future opportunities. 
                  It carries significant weight for the employee's future opportunities.
                </p>
              </div>
            </div>
          </Card>
        );

      case 8:
        return (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 sm:p-8 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A0F1E]">Review & Submit</h2>
                <p className="text-[#5B6472] text-sm">Review the record before submitting</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="grid gap-4">
                <div className="p-4 rounded-xl bg-[#F5F8FF]/30 border border-[#E8EDF5]">
                  <h3 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3">Role Overview</h3>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    <p className="text-[#0A0F1E]"><span className="text-[#5B6472]">Employee:</span> {formData.employee_name || formData.employee_email}</p>
                    <p className="text-[#0A0F1E]"><span className="text-[#5B6472]">Title:</span> {formData.job_title}</p>
                    <p className="text-[#0A0F1E]"><span className="text-[#5B6472]">Department:</span> {formData.department || 'N/A'}</p>
                    <p className="text-[#0A0F1E]"><span className="text-[#5B6472]">Period:</span> {formData.start_date} - {formData.currently_employed ? 'Present' : formData.end_date}</p>
                  </div>
                </div>
                
                {responsibilities.filter(r => r.value.trim()).length > 0 && (
                  <div className="p-4 rounded-xl bg-[#F5F8FF]/30 border border-[#E8EDF5]">
                    <h3 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3">Daily Responsibilities</h3>
                    <ul className="space-y-1">
                      {responsibilities.filter(r => r.value.trim()).map((r) => (
                        <li key={r.id} className="text-[#0A0F1E] text-sm flex items-start gap-2">
                          <span className="text-[#1B5AFF]">•</span> {r.value}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="p-4 rounded-xl bg-[#F5F8FF]/30 border border-[#E8EDF5]">
                  <h3 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3">Performance</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[#5B6472] text-sm">Overall Rating:</span>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map(r => (
                        <Star key={r} className={`w-4 h-4 ${r <= formData.rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`} />
                      ))}
                    </div>
                  </div>
                </div>
                
                {achievements.filter(a => a.value.trim()).length > 0 && (
                  <div className="p-4 rounded-xl bg-[#F5F8FF]/30 border border-[#E8EDF5]">
                    <h3 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3">Key Achievements</h3>
                    <ul className="space-y-1">
                      {achievements.filter(a => a.value.trim()).map((a) => (
                        <li key={a.id} className="text-[#0A0F1E] text-sm flex items-start gap-2">
                          <span className="text-green-400">✓</span> {a.value}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="p-4 rounded-xl bg-[#F5F8FF]/30 border border-[#E8EDF5]">
                  <h3 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3">Rehire Status</h3>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    formData.rehire_eligibility === 'yes_without_reservation' ? 'bg-green-500/20 text-green-400' :
                    formData.rehire_eligibility === 'yes_with_right_role' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {formData.rehire_eligibility === 'yes_without_reservation' ? 'Yes, without reservation' :
                     formData.rehire_eligibility === 'yes_with_right_role' ? 'Yes, with right role' : 'No'}
                  </span>
                  {formData.rehire_note && <p className="text-[#5B6472] text-sm mt-2">{formData.rehire_note}</p>}
                </div>
                
                {formData.closing_statement && (
                  <div className="p-4 rounded-xl bg-gradient-to-br from-[#1B5AFF]/5 to-transparent border border-[#1B5AFF]/20">
                    <h3 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3">Professional Assessment</h3>
                    <p className="text-[#0A0F1E] text-sm leading-relaxed italic">"{formData.closing_statement}"</p>
                  </div>
                )}
              </div>
              
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="compliance"
                    checked={formData.compliance_acknowledged}
                    onCheckedChange={(checked) => handleChange('compliance_acknowledged', checked)}
                    data-testid="compliance-checkbox"
                  />
                  <Label htmlFor="compliance" className="text-[#0A0F1E] text-sm cursor-pointer leading-relaxed">
                    I confirm that this record is accurate, based on documented performance data, and complies with EEOC guidelines. 
                    I understand that false or discriminatory information may result in legal liability.
                  </Label>
                </div>
              </div>
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  const stepLabels = [
    'Role Overview',
    'Responsibilities',
    'Performance',
    'Skills & Achievements',
    'Team Dynamics',
    formData.currently_employed ? 'Employment Status' : 'Departure & Rehire',
    'Assessment',
    'Review'
  ];

  if (initialLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="employer-record-new">
        <div className="animate-fade-in">
          {isEditMode && recordStatus === 'pending_acknowledgment' && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <p className="text-amber-400 text-sm">Editing this record will re-notify the employee to review the changes.</p>
            </div>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">{isEditMode ? 'Edit VETT Record' : 'Create VETT Record'}</h1>
          <p className="mt-2 text-[#5B6472] text-sm sm:text-base">
            {isEditMode ? 'Update this employment record.' : 'Document a verified employment record for this employee.'}
          </p>
          {lastSaved && (
            <p className="text-[#5B6472] text-xs mt-1">Draft saved {lastSaved.toLocaleTimeString()}</p>
          )}
        </div>

        <div className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#5B6472] text-sm">Step {step} of {TOTAL_STEPS}</span>
            <span className="text-[#0A0F1E] text-sm font-medium">{stepLabels[step - 1]}</span>
          </div>
          <div className="flex gap-1">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  i < step ? 'bg-[#1B5AFF]' : 'bg-[#F5F8FF]'
                }`}
              />
            ))}
          </div>
        </div>

        {renderStep()}

        <div className="flex items-center justify-between pt-2">
          {step > 1 ? (
            <Button
              onClick={handleBack}
              variant="outline"
              className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/employer/records')}
              variant="ghost"
              className="text-[#5B6472] hover:text-[#0A0F1E]"
            >
              Cancel
            </Button>
          )}
          
          {step < TOTAL_STEPS ? (
            <Button
              onClick={handleNext}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl btn-glow"
              data-testid="next-step-btn"
            >
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                onClick={() => handleSubmit(true)}
                disabled={loading}
                variant="outline"
                className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
              >
                Save Draft
              </Button>
              <Button
                onClick={() => handleSubmit(false)}
                disabled={loading}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl btn-glow"
                data-testid="submit-record-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Record'
                )}
              </Button>
            </div>
          )}
        </div>
        
        <VerificationRequiredModal
          open={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          featureName="publishing VETT Records"
        />
      </div>
    </DashboardLayout>
  );
};

export default RecordNew;
